// src/contexts/TodayCalendarContext.tsx
import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useCalendarItems } from '../features/calendar';
import { ScheduleItem } from '../types';
import { TimeBox } from '../types/timeBox';
import { Event } from '../types/calendar-event';

interface TodayCalendarContextType {
  // 數據
  scheduleItems: ScheduleItem[];
  timeBoxes: TimeBox[];
  events: Event[];
  allDayEvents: Event[];
  currentItem: ScheduleItem | null;
  nextItem: ScheduleItem | null;
  
  // 狀態
  isLoading: boolean;
  isInitialized: boolean;
  isRefreshing: boolean;
  error: Error | null;
  
  // 操作
  refresh: () => Promise<void>;
  checkConflicts: (startTime: Date, endTime: Date, excludeIds?: { timeBoxIds?: string[], eventIds?: string[] }) => Promise<{
    timeBoxConflicts: TimeBox[];
    eventConflicts: Event[];
    hasConflicts: boolean;
  }>;
}

const TodayCalendarContext = createContext<TodayCalendarContextType | null>(null);

interface TodayCalendarProviderProps {
  children: ReactNode;
}

export const TodayCalendarProvider: React.FC<TodayCalendarProviderProps> = ({ children }) => {
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // 單一數據源
  const calendarData = useCalendarItems(today, {
    useRealtime: false,
    autoRefresh: true
  });

  // 計算全天事件 - 優化依賴減少重新計算
  const allDayEvents = useMemo(() => {
    if (!calendarData.events || calendarData.events.length === 0) return [];
    return calendarData.events.filter(event => event.timeType === 'allday');
  }, [
    calendarData.events?.length,
    calendarData.events?.map(e => `${e.id}-${e.timeType}`).join(',')
  ]); // 只在事件數量或時間類型改變時重新計算

  // 穩定化 contextValue，避免不必要的重新渲染
  const contextValue = useMemo(() => ({
    scheduleItems: calendarData.scheduleItems || [],
    timeBoxes: calendarData.timeBoxes || [],
    events: calendarData.events || [],
    allDayEvents,
    currentItem: calendarData.currentItem || null,
    nextItem: calendarData.nextItem || null,
    isLoading: calendarData.isLoading || false,
    isInitialized: calendarData.isInitialized || false,
    isRefreshing: calendarData.isRefreshing || false,
    error: calendarData.error || null,
    refresh: calendarData.refresh,
    checkConflicts: calendarData.checkConflicts
  }), [
    calendarData.scheduleItems?.length,
    calendarData.timeBoxes?.length,
    calendarData.events?.length,
    calendarData.currentItem?.id,
    calendarData.nextItem?.id,
    calendarData.isLoading,
    calendarData.isInitialized,
    calendarData.isRefreshing,
    calendarData.error,
    calendarData.refresh,
    calendarData.checkConflicts,
    allDayEvents
  ]); // 使用精確的依賴減少重新渲染

  return (
    <TodayCalendarContext.Provider value={contextValue}>
      {children}
    </TodayCalendarContext.Provider>
  );
};

export const useTodayCalendarData = () => {
  const context = useContext(TodayCalendarContext);
  if (!context) {
    throw new Error('useTodayCalendarData must be used within TodayCalendarProvider');
  }
  return context;
};